import { useState } from 'react'
import { useActorName } from '@/hooks/use-actor-name'
import { Link } from '@tanstack/react-router'
import { Badge } from '@/components/ui/badge'
import {
  AlertTriangle,
  Bot,
  CircleDot,
  DollarSign,
  Mail,
  MessageCircle,
  Package,
  ShoppingCart,
  Truck,
} from 'lucide-react'
import { formatStatus, parseUTCDate } from '@/lib/utils'

// ---- Category config (shared across project-detail and global timeline) ----

// eslint-disable-next-line react-refresh/only-export-components
export const CATEGORY_CONFIG: Record<
  string,
  { icon: typeof CircleDot; color: string; bg: string; label: string }
> = {
  status: { icon: CircleDot, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Status' },
  communication: { icon: Mail, color: 'text-violet-600', bg: 'bg-violet-100', label: 'Communication' },
  shipping: { icon: Truck, color: 'text-emerald-600', bg: 'bg-emerald-100', label: 'Shipping' },
  financial: { icon: DollarSign, color: 'text-amber-600', bg: 'bg-amber-100', label: 'Financial' },
  ai: { icon: Bot, color: 'text-pink-600', bg: 'bg-pink-100', label: 'AI' },
  shopify: { icon: ShoppingCart, color: 'text-green-600', bg: 'bg-green-100', label: 'Shopify' },
  customer_communication: { icon: MessageCircle, color: 'text-teal-600', bg: 'bg-teal-100', label: 'Customer Comms' },
  action: { icon: AlertTriangle, color: 'text-orange-600', bg: 'bg-orange-100', label: 'Action' },
  system: { icon: Package, color: 'text-gray-600', bg: 'bg-gray-100', label: 'System' },
}

// eslint-disable-next-line react-refresh/only-export-components
export const DEFAULT_CATEGORY = {
  icon: CircleDot,
  color: 'text-gray-600',
  bg: 'bg-gray-100',
  label: 'Other',
}

// ---- Helpers ----

function stripHtmlTags(str: string) {
  return str.replace(/<[^>]*>/g, '')
}

// eslint-disable-next-line react-refresh/only-export-components
export function cleanTitle(raw: string) {
  return stripHtmlTags(raw).replace(/\b[a-z]+(?:_[a-z]+)+\b/g, (match) =>
    match.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function formatTimelineDate(dateStr: string) {
  return parseUTCDate(dateStr).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

// eslint-disable-next-line react-refresh/only-export-components
export function formatDayHeader(dateStr: string) {
  const d = parseUTCDate(dateStr)
  const today = new Date()
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)

  if (d.toDateString() === today.toDateString()) return 'Today'
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday'
  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

interface TimelineEventBase {
  uuid: string
  occurred_at: string
  event_category: string
  event_type: string
  title: string
  description?: string | null
  metadata_json?: string | null
  actor_name?: string | null
  actor_type?: string | null
  order?: { uuid: string; name?: string | null; order_number?: string | null } | null
  purchase_order?: { uuid: string; po_number: string } | null
  project?: { uuid: string; name: string } | null
}

// eslint-disable-next-line react-refresh/only-export-components
export function groupEventsByDay<T extends { occurred_at: string }>(events: T[]) {
  const groups: { date: string; events: T[] }[] = []
  for (const event of events) {
    const dayKey = parseUTCDate(event.occurred_at).toDateString()
    const last = groups[groups.length - 1]
    if (last && last.date === dayKey) {
      last.events.push(event)
    } else {
      groups.push({ date: dayKey, events: [event] })
    }
  }
  return groups
}

function isEmptyValue(value: unknown): boolean {
  if (value == null) return true
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed === '' || trimmed === '-' || trimmed === 'null' || trimmed === 'None'
  }
  return false
}

function formatMetadataValue(value: unknown): string {
  if (isEmptyValue(value)) return ''
  if (typeof value === 'string') {
    if (value.startsWith('[')) {
      try {
        const arr = JSON.parse(value)
        if (Array.isArray(arr)) return arr.join(', ')
      } catch {
        /* not JSON */
      }
    }
    if (/^[\d.$,]+$/.test(value) || value.includes('@') || value.includes('/')) return value
    if (value.includes('_'))
      return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    return value
  }
  if (typeof value === 'number') return String(value)
  if (Array.isArray(value)) return value.map((v) => (typeof v === 'object' && v ? JSON.stringify(v) : String(v))).join(', ')
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

/** Check whether a value is an array of objects (needs structured rendering, not a string). */
function isObjectArray(value: unknown): value is Record<string, unknown>[] {
  return Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null
}

function RenderHandlingUnits({ units }: { units: Record<string, unknown>[] }) {
  return (
    <div className="space-y-1.5">
      {units.map((hu, i) => {
        const dims = [hu.length_in, hu.width_in, hu.height_in].filter((v) => v != null)
        const commodities = Array.isArray(hu.commodities) ? hu.commodities as Record<string, unknown>[] : []
        return (
          <div key={i} className="rounded border border-border/50 px-2 py-1">
            <span className="font-medium">
              {String(hu.num_units)}{'\u00D7'} {String(hu.unit_type)}
            </span>
            {dims.length > 0 && (
              <span className="ml-2 text-muted-foreground">
                {dims.join(' \u00D7 ')}{'\u2033'}
              </span>
            )}
            {commodities.length > 0 && (
              <div className="mt-0.5 space-y-0.5 border-l-2 border-muted pl-2 text-muted-foreground">
                {commodities.map((c, j) => (
                  <div key={j}>
                    {c.description ? <span>{String(c.description)}</span> : null}
                    {c.weight_lbs != null && <span className="ml-1.5">{Number(c.weight_lbs).toLocaleString()} lbs</span>}
                    {(c.pieces as number) > 1 && <span className="ml-1.5">{String(c.pieces)} pcs</span>}
                    {c.freight_class ? <span className="ml-1.5">Class {String(c.freight_class)}</span> : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function RenderShipmentLineItems({ items }: { items: Record<string, unknown>[] }) {
  return (
    <div className="space-y-0.5">
      {items.map((li, i) => (
        <div key={i}>
          {li.model ? (
            <span className="font-medium">{String(li.model)}</span>
          ) : li.line_item_id ? (
            <span>Item #{String(li.line_item_id)}</span>
          ) : null}
          {li.quantity != null && <span className="ml-1.5 text-muted-foreground">Qty: {String(li.quantity)}</span>}
        </div>
      ))}
    </div>
  )
}

function RenderObjectArray({ items }: { items: Record<string, unknown>[] }) {
  return (
    <div className="space-y-1">
      {items.map((obj, i) => (
        <div key={i} className="rounded border border-border/50 px-2 py-1">
          {Object.entries(obj)
            .filter(([, v]) => !isEmptyValue(v) && !isObjectArray(v))
            .map(([k, v]) => (
              <span key={k} className="mr-3">
                <span className="text-muted-foreground">{k.replace(/_/g, ' ')}:</span>{' '}
                {formatMetadataValue(v)}
              </span>
            ))}
        </div>
      ))}
    </div>
  )
}

// ---- TimelineEvent component ----

export function TimelineEvent({
  event,
  showProject,
}: {
  event: TimelineEventBase
  showProject?: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const actorName = useActorName()
  const cfg = CATEGORY_CONFIG[event.event_category] || DEFAULT_CATEGORY

  let metadata: Record<string, unknown> | null = null
  if (event.metadata_json) {
    try {
      metadata = JSON.parse(event.metadata_json)
    } catch {
      /* ignore */
    }
  }

  // Flatten nested objects (e.g. "args": {category: "other", ...}) into top-level entries
  const metadataEntries: [string, unknown][] = []
  if (metadata) {
    for (const [key, value] of Object.entries(metadata)) {
      if (isEmptyValue(value)) continue
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        for (const [subKey, subVal] of Object.entries(value as Record<string, unknown>)) {
          if (!isEmptyValue(subVal)) metadataEntries.push([subKey, subVal])
        }
      } else {
        metadataEntries.push([key, value])
      }
    }
  }
  const hasDetails = metadataEntries.length > 0

  const rawTitle = cleanTitle(event.title)
  // Truncate at em-dash delimiter if present and title is long, since full text is in details
  const dashIdx = rawTitle.indexOf(' — ')
  const title =
    dashIdx > 0 && rawTitle.length > 80
      ? rawTitle.slice(0, dashIdx)
      : rawTitle.length > 120
        ? rawTitle.slice(0, 120) + '...'
        : rawTitle

  const entityLinks: { label: string; to: string; params: Record<string, string> }[] = []
  if (showProject && event.project?.uuid) {
    entityLinks.push({
      label: event.project.name,
      to: '/projects/$projectId',
      params: { projectId: event.project.uuid },
    })
  }
  if (event.order?.uuid) {
    entityLinks.push({
      label: event.order.name || `#${event.order.order_number}`,
      to: '/orders/$orderId',
      params: { orderId: event.order.uuid },
    })
  }
  if (event.purchase_order?.uuid) {
    entityLinks.push({
      label: event.purchase_order.po_number,
      to: '/purchase-orders/$poId',
      params: { poId: event.purchase_order.uuid },
    })
  }

  return (
    <div>
      <div className="mt-1">
        <p className="text-sm font-medium leading-snug">{title}</p>
        <span className="text-[11px] text-muted-foreground">
          {formatTimelineDate(event.occurred_at)}
        </span>
      </div>
      {event.description && (
        <p className="mt-0.5 text-sm text-muted-foreground">{event.description}</p>
      )}

      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <Badge variant="outline" className="text-[10px] font-normal">
          {cfg.label}
        </Badge>
        <span className="text-[10px] text-muted-foreground">
          {formatStatus(event.event_type)}
        </span>
        {event.actor_name && event.actor_type !== 'system' && (
          <span className="text-[10px] text-muted-foreground">
            &middot; {actorName(event.actor_name)}
          </span>
        )}
        {entityLinks.map((link) => (
          <Link
            key={link.to + Object.values(link.params)[0]}
            to={link.to as '/orders/$orderId'}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            params={link.params as any}
            className="text-[10px] font-medium text-primary hover:underline"
          >
            {link.label}
          </Link>
        ))}
        {hasDetails && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-[10px] font-medium text-muted-foreground hover:text-foreground"
          >
            {expanded ? 'Hide details' : 'Details'}
          </button>
        )}
      </div>

      {expanded && hasDetails && (
        <div className="mt-2 rounded-md bg-muted/50 px-3 py-2">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
            {metadataEntries.map(([key, value]) => (
              <div key={key} className="contents">
                <dt className="font-medium text-muted-foreground capitalize">
                  {key.replace(/_/g, ' ')}
                </dt>
                <dd className="text-foreground break-words">
                  {isObjectArray(value) ? (
                    key === 'handling_units' ? (
                      <RenderHandlingUnits units={value} />
                    ) : key === 'line_items' ? (
                      <RenderShipmentLineItems items={value} />
                    ) : (
                      <RenderObjectArray items={value} />
                    )
                  ) : (
                    formatMetadataValue(value)
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  )
}

// ---- TimelineList: renders grouped events with day headers and vertical line ----

export function TimelineList<T extends TimelineEventBase>({
  events,
  showProject,
}: {
  events: T[]
  showProject?: boolean
}) {
  const dayGroups = groupEventsByDay(events)

  return (
    <div className="space-y-6">
      {dayGroups.map((group) => (
        <div key={group.date}>
          <div className="sticky top-0 z-10 -mx-1 mb-3 flex items-center gap-3 rounded-lg bg-background px-2 py-1.5">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {formatDayHeader(group.events[0]!.occurred_at)}
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <div className="space-y-0">
            {group.events.map((event, idx) => {
              const cfg = CATEGORY_CONFIG[event.event_category] || DEFAULT_CATEGORY
              const EventIcon = cfg.icon
              const isLast = idx === group.events.length - 1

              return (
                <div key={event.uuid} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${cfg.bg}`}
                    >
                      <EventIcon className={`h-4 w-4 ${cfg.color}`} />
                    </div>
                    {!isLast && <div className="w-0.5 flex-1 bg-border" />}
                  </div>

                  <div className="min-w-0 flex-1 pb-6">
                    <TimelineEvent event={event} showProject={showProject} />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

// ---- CategoryFilterPills: the filter bar ----

export function CategoryFilterPills({
  events,
  activeCategory,
  onCategoryChange,
}: {
  events: { event_category: string }[]
  activeCategory: string | null
  onCategoryChange: (cat: string | null) => void
}) {
  const categories = (() => {
    if (!events.length) return []
    const counts = new Map<string, number>()
    for (const e of events) {
      counts.set(e.event_category, (counts.get(e.event_category) || 0) + 1)
    }
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1])
  })()

  return (
    <div className="flex flex-wrap gap-2">
      <button
        onClick={() => onCategoryChange(null)}
        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
          activeCategory === null
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-border hover:bg-muted'
        }`}
      >
        All
      </button>
      {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => {
        const count = categories.find(([k]) => k === key)?.[1]
        if (!count && activeCategory !== key) return null
        const CatIcon = cfg.icon
        return (
          <button
            key={key}
            onClick={() => onCategoryChange(activeCategory === key ? null : key)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              activeCategory === key
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border hover:bg-muted'
            }`}
          >
            <CatIcon className="h-3 w-3" />
            {cfg.label}
            {count != null && <span className="opacity-60">({count})</span>}
          </button>
        )
      })}
    </div>
  )
}
